/**
 * "Builds for phones" in Features › Connections: the person's build-service tokens (Expo EAS, Codemagic; connect with
 * the provider's steps, disconnect; the token never comes back) and their Macs (`flow builder start`, online /
 * offline, remove). GET /me/device-builds/connections and /runners. Nothing shows while both answers are missing.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Popconfirm, Tag } from 'antd';
import { disconnectProvider, getConnections, getRunners, removeRunner } from '../../services/DeviceBuildsService';
import { formatRelativeTime } from '../../utils/relativeTime';
import ConnectProviderPanel from './ConnectProviderPanel';
import { CONNECTION_NAME } from './deviceBuildsText';
import './DeviceBuilds.css';

const PROVIDERS = ['expo', 'codemagic'];

export default function BuildServicesSettings() {
  const [connections, setConnections] = useState(null);
  const [runners, setRunners] = useState(null);
  const [connecting, setConnecting] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    getConnections().then(setConnections).catch(() => setConnections(null));
    getRunners().then((answer) => setRunners(answer?.runners || [])).catch(() => setRunners(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!connections && !runners) { return null; }

  const act = async(work) => {
    setError(null);
    try {
      await work();
      load();
    } catch (err) {
      setError(err?.message || 'That did not work; try again.');
    }
  };

  return (
    <section className="devb-settings" data-testid="devb-settings">
      <h3>Builds for phones</h3>
      <p className="devb-muted">The build services Flow uses with your own accounts, and the Macs that build for you.</p>
      {connections && (
        <ul className="devb-list">
          {PROVIDERS.map((provider) => {
            const info = connections.providers?.[provider] || {};
            return (
              <li className="devb-item" data-testid={`devb-settings-${provider}`} key={provider}>
                <div className="devb-item-head">
                  <strong>{CONNECTION_NAME[provider]}</strong>
                  {info.connected ? <Tag color="success">Connected{info.account ? ` · ${info.account}` : ''}</Tag> : <Tag>Not connected</Tag>}
                  {info.connected ? (
                    <Popconfirm onConfirm={() => act(() => disconnectProvider(provider))} title={`Disconnect ${CONNECTION_NAME[provider]}? Builds it runs keep going.`}>
                      <Button size="small" type="link">Disconnect</Button>
                    </Popconfirm>
                  ) : connecting !== provider && <Button onClick={() => setConnecting(provider)} size="small" type="link">Connect</Button>}
                </div>
                {connecting === provider && !info.connected && (
                  <ConnectProviderPanel
                    available={connections.available !== false}
                    info={info}
                    onConnected={() => { setConnecting(null); load(); }}
                    provider={provider}
                    reason={connections.reason || null}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
      <h4>Your Macs</h4>
      {runners && runners.length > 0 ? (
        <ul className="devb-list" data-testid="devb-settings-runners">
          {runners.map((runner) => (
            <li className="devb-item" key={runner.id}>
              <div className="devb-item-head">
                <strong>{runner.name}</strong>
                <Tag color={runner.online ? 'success' : 'default'}>{runner.online ? 'Online' : 'Offline'}</Tag>
                <span className="devb-muted">{(runner.platforms || []).map((p) => (p === 'ios' ? 'iOS' : 'Android')).join(', ')} · seen {formatRelativeTime(runner.lastSeenAt)}</span>
                <Popconfirm onConfirm={() => act(() => removeRunner(runner.id))} title={`Remove ${runner.name}? Builds waiting for it go back to the queue.`}>
                  <Button size="small" type="link">Remove</Button>
                </Popconfirm>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="devb-muted" data-testid="devb-settings-no-runners">
          None yet. With the Flow CLI on a Mac, <code>flow build &lt;service&gt; --platform ios</code> builds there once, and <code>flow builder start</code> makes it a builder you can pick in Build for devices.
        </p>
      )}
      {error && <p className="devb-bad">{error}</p>}
    </section>
  );
}
